/**
 * Secure Logger Utility
 * Prevents sensitive data from being logged to console
 * 
 * Usage:
 * - Development: All logs shown
 * - Production: Only errors shown, sensitive data sanitized
 */

const isDevelopment = import.meta.env.MODE === 'development';
const isProduction = import.meta.env.MODE === 'production';

// Sensitive keys that should never be logged
const SENSITIVE_KEYS = [
  'password',
  'token',
  'apiKey',
  'api_key',
  'secret',
  'authorization',
  'auth',
  'jwt',
  'bearer',
  'credential',
  'credentials',
  'otp',
  'pin',
  'ssn',
  'creditCard',
  'cardNumber',
  'cvv',
  'privateKey',
  'private_key',
  'accessToken',
  'access_token',
  'refreshToken',
  'refresh_token',
  'sessionId',
  'session_id',
];

/**
 * Sanitize object by removing sensitive keys
 */
const sanitizeObject = (obj, depth = 0, maxDepth = 3) => {
  if (depth > maxDepth) return '[Object]';
  if (obj === null || obj === undefined) return obj;
  if (typeof obj !== 'object') return obj;

  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeObject(item, depth + 1, maxDepth));
  }

  const sanitized = {};
  for (const [key, value] of Object.entries(obj)) {
    const lowerKey = key.toLowerCase();
    const isSensitive = SENSITIVE_KEYS.some(sensitiveKey => 
      lowerKey.includes(sensitiveKey)
    );

    if (isSensitive) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof value === 'object' && value !== null) {
      sanitized[key] = sanitizeObject(value, depth + 1, maxDepth);
    } else {
      sanitized[key] = value;
    }
  }
  return sanitized;
};

/**
 * Sanitize error object
 */
const sanitizeError = (error) => {
  if (!error) return error;
  
  const sanitized = {
    message: error.message,
    name: error.name,
  };

  // Only include stack trace in development
  if (isDevelopment && error.stack) {
    sanitized.stack = error.stack;
  }

  return sanitized;
};

/**
 * Format log message with timestamp
 */
const formatMessage = (level, message, data) => {
  const timestamp = new Date().toISOString();
  const prefix = `[${timestamp}] [${level.toUpperCase()}]`;
  
  if (data) {
    return `${prefix} ${message}`;
  }
  return `${prefix} ${message}`;
};

/**
 * Main logger object
 */
export const logger = {
  /**
   * Log info messages (development only)
   */
  info: (message, data = null) => {
    if (!isDevelopment) return;
    
    const formatted = formatMessage('info', message, data);
    if (data) {
      const sanitized = sanitizeObject(data);
      console.log(formatted, sanitized);
    } else {
      console.log(formatted);
    }
  },

  /**
   * Log debug messages (development only)
   */
  debug: (message, data = null) => {
    if (!isDevelopment) return;
    
    const formatted = formatMessage('debug', message, data);
    if (data) {
      const sanitized = sanitizeObject(data);
      console.debug(formatted, sanitized);
    } else {
      console.debug(formatted);
    }
  },

  /**
   * Log warnings (all environments)
   */
  warn: (message, data = null) => {
    const formatted = formatMessage('warn', message, data);
    if (data) {
      const sanitized = sanitizeObject(data);
      console.warn(formatted, sanitized);
    } else {
      console.warn(formatted);
    }
  },

  /**
   * Log errors (all environments, sanitized)
   */
  error: (message, error = null) => {
    const formatted = formatMessage('error', message, error);
    if (error) {
      const sanitized = error instanceof Error 
        ? sanitizeError(error)
        : sanitizeObject(error);
      console.error(formatted, sanitized);
    } else {
      console.error(formatted);
    }
  },

  /**
   * Log API calls (sanitized)
   */
  api: (method, url, status = null, data = null) => {
    if (!isDevelopment) return;
    
    const message = `[API] ${method} ${url}${status ? ` - ${status}` : ''}`;
    if (data) {
      const sanitized = sanitizeObject(data);
      console.log(message, sanitized);
    } else {
      console.log(message);
    }
  },

  /**
   * Log performance metrics
   */
  performance: (label, duration) => {
    if (!isDevelopment) return;
    console.log(`[PERF] ${label}: ${duration}ms`);
  },

  /**
   * Log feature flags
   */
  feature: (featureName, enabled) => {
    if (!isDevelopment) return;
    console.log(`[FEATURE] ${featureName}: ${enabled ? 'enabled' : 'disabled'}`);
  },

  /**
   * Sanitize and log any data (for testing)
   */
  sanitize: (data) => {
    return sanitizeObject(data);
  },
};

export default logger;
